/**
 * Error model for the OpenMarkets client.
 *
 * Every failure surfaces as an {@link OpenMarketsError} so callers can branch on
 * a stable `code` (never the human `message` — see API-NOTES.md "Errors").
 * Network/transport failures are distinguished via `isNetworkError` + a
 * synthetic `network_error` code, since those carry no HTTP status.
 */
export class OpenMarketsError extends Error {
    /** Stable error code — branch on this, not `message`. */
    code;
    /** HTTP status (0 for network/transport and client-side validation errors). */
    status;
    /** `error.details` from the API envelope (e.g. `retry_after_seconds`, `window`). */
    details;
    /** True when the request never got an HTTP response (DNS/connect/reset/etc). */
    isNetworkError;
    constructor(message, init) {
        super(message);
        this.name = "OpenMarketsError";
        this.code = init.code;
        this.status = init.status;
        this.details = init.details ?? {};
        this.isNetworkError = init.isNetworkError ?? false;
        if (init.cause !== undefined) {
            this.cause = init.cause;
        }
        // Restore prototype chain for `instanceof` across transpile targets.
        Object.setPrototypeOf(this, OpenMarketsError.prototype);
    }
    /** Wrap a transport-level failure (fetch threw) as an OpenMarketsError. */
    static network(cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        return new OpenMarketsError(`Network error: ${message}`, {
            code: "network_error",
            status: 0,
            isNetworkError: true,
            cause,
        });
    }
    /** `details.retry_after_seconds` if the API supplied one, else null. */
    get retryAfterSeconds() {
        const v = this.details["retry_after_seconds"];
        return typeof v === "number" && Number.isFinite(v) ? v : null;
    }
}
/**
 * Parse an OpenMarkets error envelope (`{ error: { code, message, details } }`)
 * into an {@link OpenMarketsError}. Falls back to a synthetic `http_<status>`
 * code when the body isn't a recognizable envelope (e.g. an upstream 502 page).
 */
export function parseErrorEnvelope(status, body, rawText) {
    if (body && typeof body === "object" && "error" in body) {
        const err = body.error;
        if (err && typeof err === "object") {
            const e = err;
            const code = typeof e["code"] === "string" ? e["code"] : `http_${status}`;
            const message = typeof e["message"] === "string"
                ? e["message"]
                : `Request failed with status ${status}`;
            const details = e["details"] && typeof e["details"] === "object"
                ? e["details"]
                : {};
            return new OpenMarketsError(message, { code, status, details });
        }
    }
    const fallback = rawText && rawText.length > 0 && rawText.length < 300
        ? rawText
        : `Request failed with status ${status}`;
    return new OpenMarketsError(fallback, { code: `http_${status}`, status });
}
//# sourceMappingURL=errors.js.map